/**
 * Delivers the outbox and keeps the local database current.
 *
 * Push: messages for the signed-in account are sent strictly in creation order. Record changes travel in
 * batches to /api/sync/push, which applies them in order and stops at the first failure; files and compliance
 * documents are uploaded one at a time with idempotent PUTs. Delivery halts at any message that needs a person
 * (a conflict or a rejected change) so nothing is ever applied out of order.
 *
 * Pull: /api/sync/pull returns every change since this device's cursor, including deletions. Records that
 * still have undelivered local changes are left alone; they are reconciled when those changes are pushed.
 */
import { db, entityKey, getMeta, isNamespace, setMeta, tableFor, type DocumentOp, type FileOp, type LegacyRequestOp, type OutboxOp, type RecordOp, type Stored } from '../data/db';
import { normalize } from '../data/normalize';
import type { ComplianceDocument, EntityMap, Namespace } from '../data/types';
import { api, ApiError, NetworkError, request } from '../lib/api';
import { isOnline, subscribeConnection } from './connectivity';
import { onEnqueue, outboxOwner, ownerQueue } from './outbox';
import { setSyncActivity } from './status';

const BATCH_LIMIT = 25;
const BATCH_BYTES = 600_000;
const CURSOR_KEY = 'sync-cursor';

interface PushResult {
  opId: string;
  status: 'applied' | 'conflict' | 'forbidden' | 'invalid';
  error?: string;
  record?: { namespace: string; key: string; revision: number; deleted: boolean; updatedAt: string };
  current?: { value: unknown; revision: number; deleted?: boolean; updatedAt?: string } | null;
}

interface PullChange {
  kind: 'record' | 'document';
  seq: number;
  namespace?: string;
  key?: string;
  value?: unknown;
  revision?: number;
  deleted: boolean;
  updatedAt?: string;
  id?: string;
  document?: ComplianceDocument | null;
}

/** Thrown to stop delivery when the head of the queue needs a person or the session has expired. */
class Halt extends Error {}

let running: Promise<void> | null = null;
let rerun = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryDelay = 2000;
let pushAllowed = true;

/** Sync is paused while signed out or while the session needs re-authentication. */
export function setPushAllowed(allowed: boolean) { pushAllowed = allowed; if (allowed) requestSync(); }

function scheduleRetry() {
  if (retryTimer) return;
  retryTimer = setTimeout(() => { retryTimer = null; requestSync(); }, retryDelay);
  retryDelay = Math.min(60_000, retryDelay * 2);
}

function toWire(op: RecordOp, previousInBatch: RecordOp | undefined) {
  return { opId: op.opId, type: op.type, namespace: op.namespace, key: op.key, value: op.value, baseRevision: previousInBatch ? previousInBatch.baseRevision : op.baseRevision };
}

async function markHeadFailure(op: OutboxOp, error: unknown): Promise<never> {
  if (error instanceof NetworkError) { await db.outbox.update(op.seq!, { attempts: op.attempts + 1, lastError: error.message }); throw error; }
  if (error instanceof ApiError) {
    if (error.status === 401) throw new Halt('Session expired');
    if (error.status >= 500 || error.status === 429) { await db.outbox.update(op.seq!, { attempts: op.attempts + 1, lastError: error.message }); throw error; }
    // 400/403/404/413: retrying will not help; a person must decide.
    await db.outbox.update(op.seq!, { status: 'rejected', attempts: op.attempts + 1, lastError: error.message });
    throw new Halt(error.message);
  }
  throw error;
}

/**
 * After an applied change, re-base later queued changes to the same record onto the new revision. `bases` are
 * the revisions those later changes may still carry: the op's own base and any revision produced earlier in
 * the same batch.
 */
async function afterApplied(op: RecordOp, result: PushResult, bases: Array<number | null>) {
  const revision = result.record?.revision ?? null;
  const table = tableFor(op.namespace);
  await db.transaction('rw', table, db.outbox, async () => {
    await db.outbox.delete(op.seq!);
    const later = await db.outbox.where('entityKey').equals(op.entityKey).toArray();
    for (const item of later) if (item.kind === 'record' && bases.includes(item.baseRevision)) await db.outbox.update(item.seq!, { baseRevision: revision } as Partial<RecordOp>);
    if (op.type === 'put' && revision !== null && await table.get(op.key)) await table.update(op.key, { _rev: revision, _updatedAt: result.record?.updatedAt } as never);
  });
}

async function pushRecords(queue: OutboxOp[]) {
  const batch: RecordOp[] = [];
  let bytes = 0;
  for (const op of queue) {
    if (op.kind !== 'record' || op.status !== 'pending' || batch.length >= BATCH_LIMIT) break;
    const size = JSON.stringify(op.value ?? null).length;
    if (batch.length && bytes + size > BATCH_BYTES) break;
    batch.push(op); bytes += size;
  }
  // Ops for a record that appears earlier in the same batch are sent with that earlier op's base revision,
  // which the server re-bases as each one lands.
  const firstForKey = new Map<string, RecordOp>();
  const ops = batch.map((op) => { const previous = firstForKey.get(op.entityKey); if (!previous) firstForKey.set(op.entityKey, op); return toWire(op, previous); });
  let response: { results: PushResult[] };
  try { response = await api('/api/sync/push', { method: 'POST', body: { ops } }); }
  catch (error) { return markHeadFailure(batch[0], error); }
  const producedInBatch = new Map<string, number | null>();
  for (const [index, result] of response.results.entries()) {
    const op = batch[index];
    if (!op || result.opId !== op.opId) break;
    if (result.status === 'applied') {
      const bases = producedInBatch.has(op.entityKey) ? [op.baseRevision, producedInBatch.get(op.entityKey)!] : [op.baseRevision];
      await afterApplied(op, result, bases);
      producedInBatch.set(op.entityKey, result.record?.revision ?? null);
      continue;
    }
    if (result.status === 'conflict') await db.outbox.update(op.seq!, { status: 'conflict', server: result.current ?? null, lastError: result.error } as Partial<RecordOp>);
    else await db.outbox.update(op.seq!, { status: 'rejected', lastError: result.error || 'The server rejected this change.' });
    throw new Halt(result.status);
  }
}

async function pushFile(op: FileOp) {
  const stored = await db.blobs.get(op.fileId);
  if (!stored) { await db.outbox.update(op.seq!, { status: 'rejected', lastError: 'The file is no longer stored on this device.' }); throw new Halt('missing file'); }
  try {
    await request(`/api/files/${encodeURIComponent(op.fileId)}`, { method: 'PUT', body: stored.blob, timeoutMs: 120_000, headers: { 'Content-Type': op.meta.type || 'application/octet-stream', 'x-pcs-file-meta': encodeURIComponent(JSON.stringify(op.meta)) } });
  } catch (error) { return markHeadFailure(op, error); }
  await db.transaction('rw', db.blobs, db.outbox, async () => { await db.blobs.update(op.fileId, { uploaded: true }); await db.outbox.delete(op.seq!); });
}

async function pushDocument(op: DocumentOp) {
  const path = `/api/compliance-documents/${encodeURIComponent(op.documentId)}`;
  let document: ComplianceDocument | null = null;
  try {
    if (op.action === 'delete') {
      await request(path, { method: 'DELETE' }).catch((error) => { if (!(error instanceof ApiError && error.status === 404)) throw error; });
    } else if (op.action === 'upload') {
      const stored = op.blobId ? await db.blobs.get(op.blobId) : undefined;
      if (!stored) { await db.outbox.update(op.seq!, { status: 'rejected', lastError: 'The file is no longer stored on this device.' }); throw new Halt('missing file'); }
      document = (await api<{ document: ComplianceDocument }>(path, { method: 'PUT', body: stored.blob, timeoutMs: 120_000, headers: { 'Content-Type': op.meta?.type || 'application/octet-stream', 'x-pcs-document-meta': encodeURIComponent(JSON.stringify(op.meta)) } })).document;
    } else {
      document = (await api<{ document: ComplianceDocument }>(path, { method: 'PUT', body: op.meta })).document;
    }
  } catch (error) { if (error instanceof Halt) throw error; return markHeadFailure(op, error); }
  await db.transaction('rw', [db.documents, db.blobs, db.outbox], async () => {
    await db.outbox.delete(op.seq!);
    if (op.blobId) await db.blobs.delete(op.blobId);
    const stillQueued = await db.outbox.where('entityKey').equals(op.entityKey).count();
    if (document && !stillQueued) await db.documents.put(document);
  });
}

async function pushLegacy(op: LegacyRequestOp) {
  const stored = await db.blobs.get(op.blobId);
  try { await request(op.url, { method: op.method, headers: op.headers, body: stored?.blob, timeoutMs: 120_000 }); }
  catch (error) { return markHeadFailure(op, error); }
  await db.transaction('rw', db.blobs, db.outbox, async () => { await db.outbox.delete(op.seq!); await db.blobs.delete(op.blobId); });
}

async function push() {
  const owner = outboxOwner();
  if (!owner || !pushAllowed) return;
  for (;;) {
    const queue = await ownerQueue(owner);
    const head = queue[0];
    if (!head) return;
    if (head.status !== 'pending') throw new Halt('needs review');
    if (head.kind === 'record') await pushRecords(queue);
    else if (head.kind === 'file') await pushFile(head);
    else if (head.kind === 'document') await pushDocument(head);
    else await pushLegacy(head);
  }
}

async function applyChanges(changes: PullChange[]) {
  const writable = [db.outbox, db.documents, db.clients, db.jobs, db.technicians, db.jobRecords, db.pestActivity, db.rodentPlans];
  await db.transaction('rw', writable, async () => {
    const pending = new Set((await db.outbox.toArray()).map((op) => op.entityKey));
    for (const change of changes) {
      if (change.kind === 'document' && change.id) {
        if (pending.has(`doc/${change.id}`)) continue;
        if (change.deleted || !change.document) await db.documents.delete(change.id);
        else await db.documents.put(change.document);
        continue;
      }
      if (!change.namespace || !change.key || !isNamespace(change.namespace)) continue;
      if (pending.has(entityKey(change.namespace, change.key))) continue;
      const namespace: Namespace = change.namespace;
      const table = tableFor(namespace);
      if (change.deleted) await table.delete(change.key);
      else await table.put({ ...normalize(namespace, change.value, change.key), _rev: change.revision, _updatedAt: change.updatedAt } as Stored<EntityMap[typeof namespace]>);
    }
  });
}

async function pull() {
  let cursor = await getMeta<number>(CURSOR_KEY, 0);
  for (let page = 0; page < 200; page += 1) {
    const result = await api<{ changes: PullChange[]; cursor: number; hasMore: boolean; latest: number }>(`/api/sync/pull?since=${cursor}&limit=500`);
    if (result.latest < cursor) { cursor = 0; await setMeta(CURSOR_KEY, 0); continue; }
    await applyChanges(result.changes);
    cursor = result.cursor;
    await setMeta(CURSOR_KEY, cursor);
    await setMeta('last-synced-at', new Date().toISOString());
    if (!result.hasMore) return;
  }
}

async function cycle() {
  if (!outboxOwner() || !pushAllowed) return;
  if (!isOnline()) return;
  setSyncActivity(true);
  try {
    let halted = false;
    try { await push(); } catch (error) { if (error instanceof Halt) halted = true; else throw error; }
    await pull();
    retryDelay = 2000;
    if (halted) return;
  } catch (error) {
    if (!(error instanceof Halt)) scheduleRetry();
    if (!(error instanceof NetworkError) && !(error instanceof ApiError) && !(error instanceof Halt)) console.error('Sync failed', error);
  } finally {
    setSyncActivity(false);
  }
}

async function withLock(task: () => Promise<void>) {
  // Two open tabs must not deliver the same queue concurrently.
  if (typeof navigator !== 'undefined' && navigator.locks) return navigator.locks.request('pcs-sync', task);
  return task();
}

/** Runs a sync cycle now, or immediately after the one in progress. */
export function requestSync(): Promise<void> {
  if (running) { rerun = true; return running; }
  running = (async () => {
    do { rerun = false; await withLock(cycle); } while (rerun);
  })().finally(() => { running = null; });
  return running;
}

/** Re-downloads everything on the next sync, e.g. after discarding a change. */
export async function requestFullRefresh() {
  await setMeta(CURSOR_KEY, 0);
  return requestSync();
}

async function registerBackgroundSync() {
  try {
    const registration = await navigator.serviceWorker?.ready;
    const sync = (registration as ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } } | undefined)?.sync;
    await sync?.register('pcs-outbox');
  } catch { /* Background Sync is optional (Chromium only). */ }
}

let started = false;
export function startSyncEngine() {
  if (started || typeof window === 'undefined') return;
  started = true;
  let debounce: ReturnType<typeof setTimeout> | null = null;
  onEnqueue(() => {
    if (!isOnline()) registerBackgroundSync();
    if (debounce) clearTimeout(debounce);
    debounce = setTimeout(() => requestSync(), 250);
  });
  subscribeConnection(() => { if (isOnline()) { retryDelay = 2000; requestSync(); } });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') requestSync(); });
  navigator.serviceWorker?.addEventListener('message', (event) => { if (event.data?.type === 'pcs-sync-request') requestSync(); });
  // Pick up other devices' changes regularly while the app is open.
  setInterval(() => { if (document.visibilityState === 'visible') requestSync(); }, 60_000);
}
